"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.LedsService = void 0;
/*
 * THIS MATERIAL IS THE CONFIDENTIAL, PROPRIETARY AND TRADE SECRET PRODUCT OF
 * BLUECROSS BLUESHIELD OF SOUTH CAROLINA AND ITS SUBSIDIARIES.  ANY
 * UNAUTHORIZED USE, REPRODUCTION OR TRANSFER OF THESE MATERIALS IS STRICTLY
 * PROHIBITED.
 * COPYRIGHT 2020 BLUECROSS BLUESHIELD OF SOUTH CAROLINA   ALL RIGHTS
 * RESERVED.
 */
const enums_1 = require("../enums");
const config_1 = require("./config");
const util_1 = require("./util");
const commweb_service_1 = require("./commweb-service");
class LedsService {
    /**
     * Constructor
     *
     * This will configure and setup the LedsService.
     *
     * commWebApiConfig is used for consuming the commweb api.
     *
     * commServicesAuthConfig - commercial services authentication configuration
     *
     * @param ApiConfig commWebApiConfig
     * @param LedsEnvironment ledsEnvironment
     * @param commServicesAuthConfig CommServicesAuthConfig
     */
    constructor(commWebApiConfig, ledsEnvironment, commServicesAuthConfig) {
        this.apiConfigInitialized = false;
        this.commWebApiConfig = commWebApiConfig;
        this.ledsEnvironment = ledsEnvironment;
        this.commServicesAuthConfig = commServicesAuthConfig;
        //create an empty apiConfig
        this.apiConfig = {
            baseUrl: '',
            constituent: commWebApiConfig.constituent,
            useMockData: commWebApiConfig.useMockData,
            eventLogEnabled: commWebApiConfig.eventLogEnabled,
            commServicesUrls: commWebApiConfig.commServicesUrls
        };
        (0, config_1.setUseMock)(commWebApiConfig.useMockData);
    }
    /**
     * Generic method for consuming APIs.
     *
     * If the ApiConfig is not initialzed fully and the LEDS token is missing,
     * this will call the CommWebService to get the SAML_Assertion data
     * which is required to call the LEDS authentication service to get
     * back the token which is required for all the LEDS Api Connect calls.
     *
     * If the token is expired, then this will repeat the above steps
     * to get back a new token.
     *
     * @param ApiRequest apiRequest
     *
     */
    sendRequest(apiRequest) {
        return new Promise((resolve, reject) => {
            if (!this.apiConfigInitialized) {
                // first time the apiConfig details will not be available as some of the details
                // are retreived only after receiving SAML_Assertion response
                // get the configuration from commwebservice
                this.getOauthTokenAndSubmitLedsRequest(apiRequest).then((response) => {
                    resolve(response);
                }).catch((err) => {
                    reject(err);
                });
            }
            else {
                // apiConfig is already present
                this.sendLedsRequest(this.apiConfig, apiRequest).then((resp) => {
                    resolve(resp);
                }).catch((error) => {
                    // if the error is http status code 419 (JWT expired/ invalid)
                    // then get a new LEDS token
                    if (error.response
                        && error.response.status === enums_1.HttpErrorCode.LEDS_TOKEN_EXPIRED
                        && this.apiConfigInitialized) {
                        this.getOauthTokenAndSubmitLedsRequest(apiRequest).then((response) => {
                            resolve(response);
                        }).catch((err) => {
                            reject(err);
                        });
                    }
                    else {
                        reject(error);
                    }
                });
            }
        });
    }
    /**
     * First gets the OauthToken using SAML Assertion and then
     * calls the leds api
     *
     * @param apiRequest: ApiRequest
     */
    getOauthTokenAndSubmitLedsRequest(apiRequest) {
        return new Promise((resolve, reject) => {
            this.getOauthToken(this.ledsEnvironment.authenticationUrl).then((oauthResponse) => {
                if (oauthResponse && oauthResponse.data && oauthResponse.data.sessionToken) {
                    this.jwtFromLeds = oauthResponse.data.sessionToken;
                    // now call the leds api
                    this.sendLedsRequest(this.apiConfig, apiRequest).then((ledsResponse) => {
                        resolve(ledsResponse);
                    }).catch((err) => {
                        reject(err);
                    });
                }
                else {
                    reject((0, util_1.getUnknownError)('Error in getting LEDS JWT'));
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }
    /**
     * Calls the LEDS API
     * @param apiConfig: ApiConfig
     * @param apiRequest: apiRequest
     * @param isSessionTokenRequired: boolean default true
     */
    sendLedsRequest(apiConfig, apiRequest, isSessionTokenRequired = true) {
        const requestOptions = {
            isLedsRequest: true,
            isSessionTokenRequired: isSessionTokenRequired,
            jwtFromLeds: this.jwtFromLeds
        };
        return new Promise((resolve, reject) => {
            const config = (0, util_1.buildRequestData)(apiConfig, apiRequest, requestOptions);
            if (!config) {
                reject((0, util_1.getUnknownError)('AxiosRequestConfig is null, error in building request data'));
            }
            if (isSessionTokenRequired
                && (!this.jwtFromLeds || this.jwtFromLeds.trim().length === 0)) {
                reject((0, util_1.getUnknownError)('LEDS Access Token is Missing'));
            }
            (0, config_1.axios)(config).then((resp) => {
                resolve(resp);
            }).catch((error) => {
                reject(error);
            });
        });
    }
    /**
     * This calls the commercial web api to get the SAML Assertion
     * and then calls LEDS to validate the SAML
     * and get the JWT (sessiontoken).
     *
     * @param ledsAuthenticationUrl
     * @param apiRequest
     */
    getOauthToken(ledsAuthenticationUrl) {
        return new Promise((resolve, reject) => {
            this.getCommWebToken().then((commwebTokenResponse) => {
                if (commwebTokenResponse && commwebTokenResponse.data
                    && commwebTokenResponse.data.assertion) {
                    const samlAssertion = commwebTokenResponse.data.assertion;
                    if (!this.apiConfigInitialized) {
                        // apiConfig is not present, creating new one
                        const env = commwebTokenResponse.data.environment;
                        const baseUrl = this.ledsEnvironment.baseUrls[env.toUpperCase()];
                        if (!baseUrl) {
                            reject((0, util_1.getUnknownError)('error in determining the LEDS baseUrl'));
                        }
                        this.apiConfig = {
                            baseUrl: baseUrl,
                            clientId: this.ledsEnvironment.clientId,
                            constituent: this.commWebApiConfig.constituent,
                            useMockData: this.commWebApiConfig.useMockData,
                            timeout: this.commWebApiConfig.timeout,
                            region: commwebTokenResponse.data.region,
                            eventLogEnabled: this.commWebApiConfig.eventLogEnabled,
                            commServicesUrls: this.commWebApiConfig.commServicesUrls
                        };
                        this.apiConfigInitialized = true;
                    }
                    // now call the leds api to get the JWT/ sessionToken
                    const oauthTokenRequest = {
                        method: enums_1.HttpMethod.POST,
                        url: ledsAuthenticationUrl,
                        data: { samlAssertion: samlAssertion }
                    };
                    this.sendLedsRequest(this.apiConfig, oauthTokenRequest, false).then((ledsResponse) => {
                        resolve(ledsResponse);
                    }).catch((error) => {
                        reject(error);
                    });
                }
                else {
                    reject((0, util_1.getUnknownError)('Error in getCommWebToken'));
                }
            }).catch((error) => {
                reject(error);
            });
        });
    }
    /**
     * gets CommWebToken (SAML Assertion)
     *
     * @param apiRequest
     */
    getCommWebToken() {
        const commwebService = new commweb_service_1.CommWebService(this.commWebApiConfig);
        const samlAssertionRequest = {
            method: enums_1.HttpMethod.GET,
            url: this.commServicesAuthConfig.samlAssertionUrl,
            customHeaders: { 'Authorization': 'Bearer ' + this.commServicesAuthConfig.authToken }
        };
        return new Promise((resolve, reject) => {
            commwebService.sendRequest(samlAssertionRequest).then((resp) => {
                resolve(resp);
            }).catch((error) => {
                if (error && error.response
                    && (error.response.status === enums_1.HttpErrorCode.COMM_SERVICES_TOKEN_EXPIRED
                        || error.response.status === enums_1.HttpErrorCode.LEDS_TOKEN_EXPIRED)) {
                    // the commercial services token has expired
                    // get new one and call the saml assertion again
                    this.authenticateAndGetCommWebToken().then((response) => {
                        resolve(response);
                    }).catch((err) => {
                        // if any error is encountered, reject it blindly
                        reject(err);
                    });
                }
                else {
                    reject(error);
                }
            });
        });
    }
    /**
     * Gets the Commercial Services Enhanced JWT
     * and then calls the SAML Assertion to get the
     * CommWebToken.
     */
    authenticateAndGetCommWebToken() {
        return new Promise((resolve, reject) => {
            const commwebService = new commweb_service_1.CommWebService(this.commWebApiConfig);
            commwebService.getCommServicesEnhancedToken(this.commServicesAuthConfig).then((authResponse) => {
                const newAuthToken = authResponse.data.token;
                // set the new token in the commServicesAuthConfig
                this.commServicesAuthConfig.authToken = newAuthToken;
                const samlAssertionRequest = {
                    method: enums_1.HttpMethod.GET,
                    url: this.commServicesAuthConfig.samlAssertionUrl,
                    customHeaders: { 'Authorization': 'Bearer ' + this.commServicesAuthConfig.authToken }
                };
                // get the saml assertion with the nee auth token (enhanced)
                commwebService.sendRequest(samlAssertionRequest).then((response) => {
                    resolve(response);
                }).catch((err) => {
                    reject(err);
                });
            }).catch((error) => {
                reject(error);
            });
        });
    }
}
exports.LedsService = LedsService;
//# sourceMappingURL=leds-service.js.map